import { Facebook, Instagram, Mail, Phone, MapPin } from "lucide-react";
import logo from "@/assets/logo.webp";
import cbsLogo from "@/assets/cbs-logo.webp";
import { apiSync } from "@/lib/api";


const Footer = () => {
  const settings = apiSync.getSiteSettings();

  // Default values while loading
  const contact = settings?.contact || {
    email: "alpacadordrecht@gmail.com",
    phone: "0782200022",
    phoneDisplay: "078-22 000 22",
    address: "Noordendijk 406, 3319GM Dordrecht"
  };
  const social = settings?.social || {
    facebook: "https://facebook.com",
    instagram: "https://instagram.com",
    tiktok: "https://tiktok.com"
  };
  const footer = settings?.footer || {
    description: "De leukste alpacaboerderij van de Drechtsteden! Kom genieten tussen onze vriendelijke alpaca's.",
    copyright: "Alpacaboerderij Dordrecht. Alle rechten voorbehouden."
  };
  const openingHours = settings?.openingHours || "Woensdag t/m zondag\n11:00 - 17:00";
  const openingHoursLines = openingHours.split('\n');
  const openingHoursNoteFooter = settings?.openingHoursNoteFooter || "Alleen op reservering";
  const footerNoteLines = openingHoursNoteFooter.split('\n');
  return <footer className="w-full bg-background border-t shadow-sm shadow-black/10">
      <div className="container py-12">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* About */}
          <div className="space-y-4">
            <a href="/" className="inline-block">
              <img src={logo} alt="Alpacaboerderij Dordrecht" className="h-24 w-auto transition-transform hover:scale-105" />
            </a>
            <p className="text-sm text-muted-foreground whitespace-pre-line">
              {footer.description}
            </p>
          </div>

          {/* Quick Links */}
          <nav className="space-y-4 md:max-lg:text-right" aria-label="Footer navigatie">
            <h3 className="font-bold">Snelle links</h3>
            <ul className="flex flex-col space-y-2 text-sm">
              <li>
                <a href="/" className="text-muted-foreground hover:text-primary transition-colors">
                  Home
                </a>
              </li>
              <li>
                <a href="/activiteiten" className="text-muted-foreground hover:text-primary transition-colors">
                  Activiteiten
                </a>
              </li>
              <li>
                <a href="/over-ons" className="text-muted-foreground hover:text-primary transition-colors">
                  Over ons
                </a>
              </li>
              <li>
                <a href="/samenwerkingen" className="text-muted-foreground hover:text-primary transition-colors">
                  Samenwerkingen
                </a>
              </li>
              <li>
                <a href="/alpacas" className="text-muted-foreground hover:text-primary transition-colors">
                  Onze Alpaca's
                </a>
              </li>
              <li>
                <a href="/faq" className="text-muted-foreground hover:text-primary transition-colors">Veelgestelde vragen</a>
              </li>
              <li>
                <a href="/contact" className="text-muted-foreground hover:text-primary transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          {/* Contact */}
          <address className="space-y-4 not-italic">
            <h3 className="font-bold">Contact</h3>
            <ul className="flex flex-col space-y-3 text-sm text-muted-foreground">
              <li>
                <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Mail size={16} aria-hidden="true" />
                  {contact.email}
                </a>
              </li>
              <li>
                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Phone size={16} aria-hidden="true" />
                  {contact.phoneDisplay}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin size={16} aria-hidden="true" />
                {contact.address}
              </li>
            </ul>
          </address>

          {/* Social & Opening Hours */}
          <div className="space-y-4 md:max-lg:text-right">
            <h3 className="font-bold">Volg ons</h3>
            <div className="flex space-x-3 md:max-lg:justify-end">
              {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all" aria-label="Volg ons op Facebook">
                  <Facebook size={20} aria-hidden="true" />
                </a>}
              {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all" aria-label="Volg ons op Instagram">
                  <Instagram size={20} aria-hidden="true" />
                </a>}
              {social.tiktok && <a href={social.tiktok} target="_blank" rel="noopener noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all" aria-label="Volg ons op TikTok">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.7a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.13z"/></svg>
                </a>}
            </div>
            <div className="text-sm">
              <p className="font-medium text-foreground">Openingstijden</p>
              <div className="text-muted-foreground mt-1">
                {openingHoursLines.map((line, index) => <p key={index}>{line || '\u00A0'}</p>)}
              </div>
              {footerNoteLines.length > 0 && <div className="text-muted-foreground mt-2 text-xs italic">
                  {footerNoteLines.map((line, index) => <p key={index}>{line || '\u00A0'}</p>)}
                </div>}
            </div>
          </div>
        </div>

        <div id="footer-separator" className="mt-12 border-t border-border pt-8 text-center text-sm text-muted-foreground">
          <p className="flex items-center justify-center gap-2 flex-wrap">
            <span>© {new Date().getFullYear()} {footer.copyright}</span>
            <span className="flex items-center gap-1">
              Made by
              <a 
                href="https://www.cb-services.nl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center hover:opacity-80 transition-opacity"
              >
                <img 
                  src={cbsLogo} 
                  alt="CB Services" 
                  style={{ width: '160px', height: '40px' }}
                />
              </a>
            </span>
          </p>
        </div>
      </div>
    </footer>;
};
export default Footer;
